import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  TextField,
  Box,
  Chip,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Typography,
  InputAdornment,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  Search as SearchIcon,
  GetApp as DownloadIcon,
  Clear as ClearIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Article as ArticleIcon,
  Storage as StorageIcon,
  StorageOutlined as StorageOutlinedIcon
} from '@mui/icons-material';
import apiService from '../services/api';

interface FileDocument {
  id: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
  metadata: {
    file_size?: number;
    custom_fields?: {
      public_url?: string;
      file_path?: string;
    };
  };
  source_type: string;
  in_knowledge_base?: boolean;
}

interface EnhancedFilesTableProps {
  files: FileDocument[];
  title?: string;
  onLoadMore?: (page: number, perPage: number, search?: string) => Promise<{documents: FileDocument[], total: number}>;
  totalCount?: number;
  serverSidePagination?: boolean;
  hideColumns?: string[];
  showKbToggle?: boolean;
  onKbToggle?: (fileId: string, inKb: boolean) => void;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof FileDocument | 'size' | 'actions';
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'title', label: 'File Name', numeric: false, sortable: true },
  { id: 'size', label: 'Size', numeric: true, sortable: true },
  { id: 'status', label: 'Status', numeric: false, sortable: true },
  { id: 'source_type', label: 'Source', numeric: false, sortable: true },
  { id: 'created_at', label: 'Upload Date', numeric: false, sortable: true },
  { id: 'actions', label: 'Actions', numeric: false, sortable: false },
];

const EnhancedFilesTable: React.FC<EnhancedFilesTableProps> = ({ 
  files,
  hideColumns = [],
  title = "Uploaded Files", 
  onLoadMore,
  totalCount = 0,
  serverSidePagination = false,
  showKbToggle = false,
  onKbToggle
}) => {
  const [viewMode, setViewMode] = useState<'table' | 'gallery'>('table');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<string>('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [allFiles, setAllFiles] = useState<FileDocument[]>(files);

  const [serverTotal, setServerTotal] = useState(totalCount);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync files prop with local state
  useEffect(() => {
    setAllFiles(files);
  }, [files]);

  useEffect(() => {
    setServerTotal(totalCount);
  }, [totalCount]);

  // Debounced server-side search
  const doServerSearch = useCallback(async (term: string) => {
    if (!serverSidePagination || !onLoadMore) return;
    setLoading(true);
    try {
      const response = await onLoadMore(1, rowsPerPage, term || undefined);
      setAllFiles(response.documents);
      setServerTotal(response.total);
      setPage(0);
    } catch (error) {
      console.error('Error searching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [serverSidePagination, onLoadMore, rowsPerPage]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (serverSidePagination) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => doServerSearch(value), 400);
    }
  }, [serverSidePagination, doServerSearch]);

  // Get unique values for filters
  const uniqueStatuses = useMemo(() => {
    const statusSet = new Set(files.map(file => file.status));
    const statuses = Array.from(statusSet);
    return statuses.filter(Boolean);
  }, [files]);

  const uniqueSources = useMemo(() => {
    const sourceSet = new Set(files.map(file => file.source_type));
    const sources = Array.from(sourceSet);
    return sources.filter(Boolean);
  }, [files]);

  // Helper functions
  const getFileSize = (file: FileDocument): number => {
    return file.metadata?.file_size || 0;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.toLowerCase().split('.').pop();
    switch (ext) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 80, color: '#d32f2f' }} />;
      case 'doc':
      case 'docx':
        return <ArticleIcon sx={{ fontSize: 80, color: '#1976d2' }} />;
      case 'xls':
      case 'xlsx':
        return <DescriptionIcon sx={{ fontSize: 80, color: '#388e3c' }} />;
      case 'ppt':
      case 'pptx':
        return <DescriptionIcon sx={{ fontSize: 80, color: '#f57c00' }} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'webp':
        return <DescriptionIcon sx={{ fontSize: 80, color: '#7b1fa2' }} />;
      default:
        return <DescriptionIcon sx={{ fontSize: 80, color: 'text.secondary' }} />;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'processing': return 'info';
      case 'failed': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  // Sorting logic
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortComparator = (a: FileDocument, b: FileDocument): number => {
    let aVal: any, bVal: any;
    
    switch (orderBy) {
      case 'title':
        aVal = a.title?.toLowerCase() || '';
        bVal = b.title?.toLowerCase() || '';
        break;
      case 'size':
        aVal = getFileSize(a);
        bVal = getFileSize(b);
        break;
      case 'status':
        aVal = a.status?.toLowerCase() || '';
        bVal = b.status?.toLowerCase() || '';
        break;
      case 'source_type':
        aVal = a.source_type?.toLowerCase() || '';
        bVal = b.source_type?.toLowerCase() || '';
        break;
      case 'created_at':
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
        break;
      default:
        return 0;
    }

    if (bVal < aVal) return order === 'asc' ? 1 : -1;
    if (bVal > aVal) return order === 'asc' ? -1 : 1;
    return 0;
  };

  // Filtering and sorting
  const filteredAndSortedFiles = useMemo(() => {
    const sourceFiles = serverSidePagination ? allFiles : files;
    let filtered = sourceFiles.filter(file => {
      const matchesSearch = !searchTerm || 
        file.title?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || file.status === statusFilter;
      const matchesSource = sourceFilter === 'all' || file.source_type === sourceFilter;
      
      return matchesSearch && matchesStatus && matchesSource;
    });

    return filtered.sort(sortComparator);
  }, [files, searchTerm, statusFilter, sourceFilter, order, orderBy, sortComparator]);

  // Pagination
  const paginatedFiles = useMemo(() => {
    if (serverSidePagination) {
      // Server handles pagination, return filtered data as-is
      return filteredAndSortedFiles;
    } else {
      // Client-side pagination
      const startIndex = page * rowsPerPage;
      return filteredAndSortedFiles.slice(startIndex, startIndex + rowsPerPage);
    }
  }, [filteredAndSortedFiles, page, rowsPerPage, serverSidePagination]);

  const handleChangePage = async (event: unknown, newPage: number) => {
    if (serverSidePagination && onLoadMore) {
      setLoading(true);
      try {
        const response = await onLoadMore(newPage + 1, rowsPerPage, searchTerm || undefined);
        setAllFiles(response.documents);
        setServerTotal(response.total);
      } catch (error) {
        console.error('Error loading more documents:', error);
      } finally {
        setLoading(false);
      }
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    
    if (serverSidePagination && onLoadMore) {
      setLoading(true);
      try {
        const response = await onLoadMore(1, newRowsPerPage, searchTerm || undefined);
        setAllFiles(response.documents);
        setServerTotal(response.total);
      } catch (error) {
        console.error('Error loading documents with new page size:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setPage(0);
    if (serverSidePagination) {
      doServerSearch('');
    }
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'all' || sourceFilter !== 'all';

  if (files.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No files uploaded yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%' }}>
      {/* Header with title and filters */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="div">
            {title} ({filteredAndSortedFiles.length})
          </Typography>
          {hasActiveFilters && (
            <Tooltip title="Clear all filters">
              <IconButton onClick={clearFilters} size="small">
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Filters Row */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="table">
              <Tooltip title="Table View">
                <ViewListIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="gallery">
              <Tooltip title="Gallery View">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField
            size="small"
            placeholder="Search files..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200, flex: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              {uniqueStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {uniqueSources.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="all">All Sources</MenuItem>
                {uniqueSources.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source.charAt(0).toUpperCase() + source.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {/* Gallery View */}
      {viewMode === 'gallery' && (
        <Box sx={{ 
          p: 2,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2
        }}>
          {paginatedFiles.map((file) => {
            const fileSize = getFileSize(file);
            const publicUrl = file.metadata?.custom_fields?.public_url;
            
            return (
              <Card key={file.id} sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}>
                {/* File Icon */}
                <Box sx={{ 
                  p: 3, 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  bgcolor: 'grey.50',
                  minHeight: 140
                }}>
                  {getFileIcon(file.title)}
                </Box>
                
                {/* File Info */}
                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  <Tooltip title={file.title}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        mb: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {file.title}
                    </Typography>
                  </Tooltip>
                  
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    <Chip
                      label={file.status || 'unknown'}
                      color={getStatusColor(file.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                    />
                    {!hideColumns.includes('source_type') && (
                      <Chip
                        label={file.source_type}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" display="block">
                    {fileSize > 0 ? formatFileSize(fileSize) : 'Size unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {formatDate(file.created_at)}
                  </Typography>
                </CardContent>
                
                {/* Actions */}
                {publicUrl && (
                  <CardActions sx={{ justifyContent: 'center', pt: 0 }}>
                    <Tooltip title="Download file">
                      <IconButton
                        component={Link}
                        href={publicUrl}
                        target="_blank"
                        rel="noopener"
                        size="small"
                        color="primary"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                )}
              </Card>
            );
          })}
        </Box>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {headCells.filter(cell => !hideColumns.includes(cell.id)).map((headCell) => (
                <TableCell
                  key={headCell.id}
                  align={headCell.numeric ? 'right' : 'left'}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedFiles.map((file) => {
              const fileSize = getFileSize(file);
              const publicUrl = file.metadata?.custom_fields?.public_url;
              
              return (
                <TableRow hover key={file.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {file.title}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {fileSize > 0 ? formatFileSize(fileSize) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={file.status || 'unknown'}
                      color={getStatusColor(file.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  {!hideColumns.includes('source_type') && (
                    <TableCell>
                      <Chip
                        label={file.source_type}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {formatDate(file.created_at)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {showKbToggle && (
                        <Tooltip title={file.in_knowledge_base !== false ? 'In Knowledge Base (click to remove)' : 'Not in KB (click to add)'}>
                          <IconButton
                            size="small"
                            onClick={() => onKbToggle?.(file.id, file.in_knowledge_base === false)}
                            sx={{
                              color: file.in_knowledge_base !== false ? 'success.main' : 'grey.400',
                              '&:hover': {
                                color: file.in_knowledge_base !== false ? 'error.main' : 'success.main',
                              },
                            }}
                          >
                            {file.in_knowledge_base !== false ? <StorageIcon fontSize="small" /> : <StorageOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {publicUrl && (
                        <Tooltip title="Download file">
                          <IconButton
                            component={Link}
                            href={publicUrl}
                            target="_blank"
                            rel="noopener"
                            size="small"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 200]}
        component="div"
        count={serverSidePagination ? serverTotal : filteredAndSortedFiles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default EnhancedFilesTable;
